import { useId, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { requestCenterApi } from '@/services/request-center-api'
import { AutocompleteField } from './AutocompleteField'

const PROVIDER_DATA_SOURCE_KEY = 'providers.all'
const PROVIDER_FETCH_LIMIT = 100

const ACCESS_LEVEL_LABEL_BY_VALUE: Record<string, string> = {
	admin: 'Admin',
	writer: 'Writer',
	viewer: 'Viewer',
}

const ACCESS_LEVEL_OPTIONS: Array<{ label: string; value: string }> = [
	{ label: 'Admin', value: 'admin' },
	{ label: 'Writer', value: 'writer' },
	{ label: 'Viewer', value: 'viewer' },
]

const DURATION_OPTIONS: Array<{ label: string; value: number }> = [
	{ label: '30 days', value: 30 },
	{ label: '90 days', value: 90 },
	{ label: '180 days', value: 180 },
	{ label: '365 days', value: 365 },
]

export interface ProviderAccessEntry {
	provider: string
	accessLevel: string
	/** Set when the host field carries `withDuration: true`. */
	durationDays?: number
}

interface Props {
	label: string
	required?: boolean
	description?: string
	disabled?: boolean
	/**
	 * When true, each row also carries a duration (preset list: 30/90/180/365).
	 * The add-button waits on all three fields being set before enabling.
	 */
	withDuration?: boolean
	value: ProviderAccessEntry[]
	onChange: (next: ProviderAccessEntry[]) => void
}

/**
 * Repeating picker for access bundles. Two shapes:
 *   - `{ provider, accessLevel }` (default — used by Team Member Onboarding provider access)
 *   - `{ provider, accessLevel, durationDays }` when `withDuration` is true
 *     (used by Partner Onboarding access scope + level + duration).
 */
export function ProviderAccessListField({
	label,
	required,
	description,
	disabled,
	withDuration = false,
	value,
	onChange,
}: Props) {
	const { data: providerLookup } = useQuery({
		queryKey: ['data-source-search', PROVIDER_DATA_SOURCE_KEY, '', PROVIDER_FETCH_LIMIT],
		queryFn: () => requestCenterApi.searchDataSource(PROVIDER_DATA_SOURCE_KEY, { limit: PROVIDER_FETCH_LIMIT }),
		staleTime: 5 * 60 * 1000,
	})
	const providerNameById = useMemo(() => {
		const map = new Map<string, string>()
		for (const it of providerLookup?.data ?? []) map.set(it.value, it.name ?? it.label)
		return map
	}, [providerLookup])

	const addEntry = (entry: ProviderAccessEntry) => onChange([...value, entry])
	const removeAt = (idx: number) => onChange(value.filter((_v, i) => i !== idx))

	return (
		<div className="space-y-2">
			<FieldHeading label={label} required={required} description={description} />
			{value.length > 0 ? (
				<EntryList
					label={label}
					entries={value}
					providerNameById={providerNameById}
					disabled={disabled}
					onRemove={removeAt}
				/>
			) : null}
			<AddEntryRow disabled={disabled} withDuration={withDuration} onAdd={addEntry} />
		</div>
	)
}

function FieldHeading({ label, required, description }: { label: string; required?: boolean; description?: string }) {
	return (
		<>
			<div className="text-sm font-medium leading-none text-foreground">
				{label}
				{required ? (
					<span aria-hidden className="ml-0.5 text-destructive">
						*
					</span>
				) : null}
			</div>
			{description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
		</>
	)
}

function EntryList({
	label,
	entries,
	providerNameById,
	disabled,
	onRemove,
}: {
	label: string
	entries: ProviderAccessEntry[]
	providerNameById: Map<string, string>
	disabled?: boolean
	onRemove: (idx: number) => void
}) {
	return (
		<ul className="space-y-2" aria-label={`${label} — selected entries`}>
			{entries.map((entry, idx) => (
				<li
					key={`${entry.provider}:${entry.accessLevel}:${entry.durationDays ?? 'x'}:${idx}`}
					className="flex items-center gap-2 rounded-md border border-input bg-muted/30 px-3 py-2 text-sm"
				>
					<span className="flex-1">
						<EntryPreview
							providerLabel={providerNameById.get(entry.provider) ?? entry.provider}
							accessLevel={entry.accessLevel}
							durationDays={entry.durationDays}
						/>
					</span>
					<button
						type="button"
						aria-label="Remove entry"
						onClick={() => onRemove(idx)}
						disabled={disabled}
						className="text-muted-foreground hover:text-foreground"
					>
						<X className="h-4 w-4" />
					</button>
				</li>
			))}
		</ul>
	)
}

function AddEntryRow({
	disabled,
	withDuration,
	onAdd,
}: {
	disabled?: boolean
	withDuration: boolean
	onAdd: (entry: ProviderAccessEntry) => void
}) {
	const [draftProvider, setDraftProvider] = useState<string | undefined>()
	const [draftAccessLevel, setDraftAccessLevel] = useState<string>('')
	const [draftDuration, setDraftDuration] = useState<string>('')

	const canAdd = Boolean(draftProvider && draftAccessLevel && (!withDuration || draftDuration))
	const gridCols = withDuration ? 'md:grid-cols-[1fr_auto_auto_auto]' : 'md:grid-cols-[1fr_auto_auto]'

	const handleAdd = () => {
		if (!canAdd || !draftProvider) return
		const entry: ProviderAccessEntry = { provider: draftProvider, accessLevel: draftAccessLevel }
		if (withDuration) entry.durationDays = Number(draftDuration)
		onAdd(entry)
		setDraftProvider(undefined)
		setDraftAccessLevel('')
		setDraftDuration('')
	}

	return (
		<div className={`grid grid-cols-1 items-end gap-2 ${gridCols}`}>
			<AutocompleteField
				dataSourceKey="providers.all"
				label="Provider"
				disabled={disabled}
				value={draftProvider}
				onChange={(next) => setDraftProvider(next)}
			/>
			<PresetSelect
				label="Access level"
				value={draftAccessLevel}
				disabled={disabled}
				onChange={setDraftAccessLevel}
				options={ACCESS_LEVEL_OPTIONS}
			/>
			{withDuration ? (
				<PresetSelect
					label="Duration"
					value={draftDuration}
					disabled={disabled}
					onChange={setDraftDuration}
					options={DURATION_OPTIONS.map((o) => ({ label: o.label, value: String(o.value) }))}
				/>
			) : null}
			<div>
				<Button type="button" variant="outline" onClick={handleAdd} disabled={disabled || !canAdd}>
					<Plus className="mr-1 h-4 w-4" /> Add
				</Button>
			</div>
		</div>
	)
}

function PresetSelect({
	label,
	value,
	disabled,
	onChange,
	options,
}: {
	label: string
	value: string
	disabled?: boolean
	onChange: (next: string) => void
	options: Array<{ label: string; value: string }>
}) {
	// Native <select> popup anchoring broke when the control was wrapped in a
	// <label> (the browser measured the inline-label bounding rect instead of
	// the select itself, so the popup rendered detached at the viewport edge).
	// Use a <div> + <label htmlFor> instead.
	const id = useId()
	return (
		<div className="space-y-1">
			<label htmlFor={id} className="block text-sm font-medium leading-none text-foreground">
				{label}
			</label>
			<select
				id={id}
				disabled={disabled}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
			>
				<option value="">Select…</option>
				{options.map((o) => (
					<option key={o.value} value={o.value}>
						{o.label}
					</option>
				))}
			</select>
		</div>
	)
}

function EntryPreview({
	providerLabel,
	accessLevel,
	durationDays,
}: {
	providerLabel: string
	accessLevel: string
	durationDays?: number
}) {
	return (
		<span>
			<span className="text-sm">{providerLabel}</span>
			<span className="mx-2 text-muted-foreground">·</span>
			<span className="text-xs uppercase tracking-wide text-muted-foreground">
				{ACCESS_LEVEL_LABEL_BY_VALUE[accessLevel] ?? accessLevel}
			</span>
			{durationDays !== undefined ? (
				<>
					<span className="mx-2 text-muted-foreground">·</span>
					<span className="text-xs text-muted-foreground">{durationDays} days</span>
				</>
			) : null}
		</span>
	)
}
